import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  Filter,
  Loader2,
  MessageSquareText,
  RefreshCw,
  Search,
  Star,
  UserRound,
  UsersRound,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { exportAdminFeedback, listAdminFeedback } from '@/lib/api/feedback';
import type { AdminFeedbackRecord, ListAdminFeedbackParams } from '@/types/feedback';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

const FEEDBACK_CONTEXT_OPTIONS = [
  { value: 'overall_application', label: 'Overall Application' },
  { value: 'client_onboarding', label: 'Client Onboarding' },
  { value: 'client_details_dietary_goals', label: 'Client Details - Goals and Preferences' },
  { value: 'target_nutritional_analysis', label: 'Target Nutritional Analysis' },
  { value: 'ai_meal_planning_overall', label: 'AI Meal Planning Overall' },
  { value: 'automated_meal_planning_overall', label: 'Automated Meal Planning Overall' },
  { value: 'manual_meal_planning_overall', label: 'Manual Meal Planning Overall' },
  { value: 'ai_meal_recipe_quality', label: 'AI Meal - Recipe Quality' },
  { value: 'ai_meal_nutritional_analysis', label: 'AI Meal - Nutritional Analysis' },
  { value: 'auto_meal_nutrition_analysis', label: 'Auto Meal - Nutrition Analysis' },
  { value: 'manual_meal_nutrition_analysis', label: 'Manual Meal - Nutrition Analysis' },
  { value: 'custom_recipe', label: 'Custom Recipe' },
  { value: 'nutritionist_profile', label: 'Nutritionist Profile' },
  { value: 'manual_meal_plan_generation', label: 'Manual Meal Plan Generation' },
  { value: 'ai_meal_plan_generation', label: 'AI Meal Plan Generation' },
  { value: 'auto_meal_plan_generation', label: 'Auto Meal Plan Generation' },
] as const;

const contextLabelMap = new Map(FEEDBACK_CONTEXT_OPTIONS.map((option) => [option.value, option.label]));

const formatDateTime = (value: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const formatDateOnly = (value: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const formatNumber = (value: number): string =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Number(value || 0));

const formatContext = (value: string | null): string => {
  if (!value) return '-';
  return contextLabelMap.get(value) || value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatShortId = (value: string | null): string => {
  if (!value) return '-';
  return value.length > 14 ? `${value.slice(0, 6)}...${value.slice(-4)}` : value;
};

const getInitials = (value: string | null): string => {
  const source = (value || '').trim();
  if (!source) return '?';
  const namePart = source.split('@')[0].replace(/[._-]+/g, ' ');
  const parts = namePart.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || error.response?.data?.error || error.message || fallback;
  }

  return error instanceof Error ? error.message : fallback;
};

const FeedbackStat: React.FC<{
  label: string;
  value: string | number;
  detail: string;
  icon: React.ComponentType<{ className?: string }>;
  iconClassName: string;
  barClassName: string;
}> = ({ label, value, detail, icon: Icon, iconClassName, barClassName }) => (
  <div className="relative overflow-hidden rounded-lg border border-[#dbe8ed] bg-white p-4 shadow-sm">
    <span className={`absolute inset-x-0 top-0 h-1 ${barClassName}`} />
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-[0.06em] text-[#17262f]">{label}</p>
        <p className="mt-2 truncate text-2xl font-bold leading-none text-[#12303a]">{value}</p>
      </div>
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconClassName}`}>
        <Icon className="h-5 w-5" />
      </span>
    </div>
    <p className="mt-3 text-sm text-[#5d6f78]">{detail}</p>
  </div>
);

const feedbackStateMeta = (item: AdminFeedbackRecord) => {
  if (item.passFail === 'fail') {
    return {
      label: 'Needs review',
      icon: AlertCircle,
      badgeClassName: 'border-[#ffc9c9] bg-[#fff1f1] text-[#b42318]',
      rowClassName: 'border-l-[#f04438] bg-[#fffafa] hover:bg-[#fff7f7]',
      avatarClassName: 'bg-[#fff1f1] text-[#b42318]',
    };
  }

  if (item.passFail === 'pass') {
    return {
      label: 'Passed',
      icon: CheckCircle2,
      badgeClassName: 'border-[#bde9d6] bg-[#eefbf5] text-[#167a55]',
      rowClassName: 'border-l-[#3fbf7f] hover:bg-[#fbfdfe]',
      avatarClassName: 'bg-[#eefbf5] text-[#167a55]',
    };
  }

  return {
    label: 'Unmarked',
    icon: MessageSquareText,
    badgeClassName: 'border-[#d7e6eb] bg-white text-[#6b7c85]',
    rowClassName: 'border-l-transparent hover:bg-[#fbfdfe]',
    avatarClassName: 'bg-[#eef5f8] text-[#167f9d]',
  };
};

const RatingDisplay: React.FC<{ rating: number | null }> = ({ rating }) => {
  const numericRating = Number(rating || 0);
  const filledStars = Number.isFinite(numericRating) ? Math.max(0, Math.min(5, Math.round(numericRating))) : 0;

  if (!rating) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md border border-[#d7e6eb] bg-white px-2.5 py-1 text-sm font-semibold text-[#6b7c85]">
        <Star className="h-3.5 w-3.5 text-[#a7b4ba]" />
        No rating
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-2 rounded-md border border-[#f0d79c] bg-[#fffaf0] px-2.5 py-1 text-sm font-semibold text-[#7a4d00]">
      <span className="flex items-center gap-0.5" aria-hidden="true">
        {Array.from({ length: 5 }).map((_, index) => (
          <Star
            key={index}
            className={`h-3.5 w-3.5 ${index < filledStars ? 'fill-[#d99511] text-[#d99511]' : 'text-[#d8c8a7]'}`}
          />
        ))}
      </span>
      {rating}/5
    </span>
  );
};

const NutritionistFeedbackPage: React.FC = () => {
  const [items, setItems] = useState<AdminFeedbackRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [nutritionistCount, setNutritionistCount] = useState(0);
  const [failCount, setFailCount] = useState(0);
  const [search, setSearch] = useState('');
  const [nutritionistEmail, setNutritionistEmail] = useState('');
  const [feedbackType, setFeedbackType] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const queryParams = useMemo<ListAdminFeedbackParams>(() => ({
    page,
    pageSize,
    search: search.trim() || undefined,
    nutritionistEmail: nutritionistEmail.trim() || undefined,
    feedbackType: feedbackType === 'all' ? undefined : feedbackType,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  }), [dateFrom, dateTo, feedbackType, nutritionistEmail, page, pageSize, search]);

  const loadFeedback = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await listAdminFeedback(queryParams);
      setItems(response.data.items);
      setTotal(response.data.total);
      setNutritionistCount(response.data.summary.nutritionistCount);
      setFailCount(response.data.summary.failCount);
    } catch (error) {
      toast({
        title: 'Feedback failed to load',
        description: getErrorMessage(error, 'Unable to load feedback right now.'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    void loadFeedback();
  }, [loadFeedback]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageStartIndex = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEndIndex = Math.min(total, page * pageSize);
  const ratedItems = useMemo(
    () => items.filter((item) => typeof item.rating === 'number' && Number.isFinite(item.rating)),
    [items]
  );
  const averageRating = ratedItems.length
    ? ratedItems.reduce((sum, item) => sum + Number(item.rating || 0), 0) / ratedItems.length
    : null;
  const hasActiveFilters = Boolean(
    search.trim() || nutritionistEmail.trim() || feedbackType !== 'all' || dateFrom || dateTo
  );
  const activeFilterChips = useMemo(() => {
    const chips: string[] = [];
    const trimmedSearch = search.trim();
    const trimmedEmail = nutritionistEmail.trim();

    if (trimmedSearch) chips.push(`Search: ${trimmedSearch}`);
    if (trimmedEmail) chips.push(`Nutritionist: ${trimmedEmail}`);
    if (feedbackType !== 'all') chips.push(formatContext(feedbackType));
    if (dateFrom) chips.push(`From ${formatDateOnly(dateFrom)}`);
    if (dateTo) chips.push(`To ${formatDateOnly(dateTo)}`);

    return chips;
  }, [dateFrom, dateTo, feedbackType, nutritionistEmail, search]);

  const resetPage = () => setPage(1);

  const clearFilters = () => {
    setSearch('');
    setNutritionistEmail('');
    setFeedbackType('all');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { blob, filename } = await exportAdminFeedback({
        ...queryParams,
        page: undefined,
        pageSize: undefined,
      });
      downloadBlob(blob, filename);
      toast({
        title: 'Export started',
        description: 'The filtered feedback CSV is ready.',
      });
    } catch (error) {
      toast({
        title: 'Export failed',
        description: getErrorMessage(error, 'Unable to export feedback right now.'),
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-7.5rem)] overflow-hidden rounded-[28px] border border-[#dbe6eb] bg-white px-4 py-5 sm:px-6 lg:px-8">
      <div className="w-full space-y-5">
        <section className="flex flex-col gap-4 border-b border-[#dbe8ed] pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <Badge className="w-fit border-[#bfe4ed] bg-white text-[#167f9d]" variant="outline">
              Admin review
            </Badge>
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-[#12303a] sm:text-3xl">Feedback review</h1>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-[#4b626c]">
              Review nutritionist feedback, failed checks, ratings, and context-specific notes.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button type="button" variant="outline" className="justify-center bg-white" onClick={() => void loadFeedback()} disabled={isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </Button>
            <Button type="button" className="justify-center" onClick={() => void handleExport()} disabled={isExporting || isLoading}>
              {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export CSV
            </Button>
          </div>
        </section>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <FeedbackStat
            label="Filtered rows"
            value={formatNumber(total)}
            detail="Matching current filters"
            icon={MessageSquareText}
            iconClassName="bg-[#eaf6fa] text-[#167f9d]"
            barClassName="bg-[#2eb4e4]"
          />
          <FeedbackStat
            label="Nutritionists"
            value={formatNumber(nutritionistCount)}
            detail="Unique submitters"
            icon={UsersRound}
            iconClassName="bg-[#eef3ff] text-[#4568c8]"
            barClassName="bg-[#5877c7]"
          />
          <FeedbackStat
            label="Needs review"
            value={formatNumber(failCount)}
            detail="Marked as failed"
            icon={AlertCircle}
            iconClassName="bg-[#fff1f1] text-[#b42318]"
            barClassName="bg-[#f04438]"
          />
          <FeedbackStat
            label="Average rating"
            value={averageRating === null ? '-' : `${averageRating.toFixed(1)}/5`}
            detail={ratedItems.length > 0 ? `${ratedItems.length} rated rows visible` : 'No ratings visible'}
            icon={Star}
            iconClassName="bg-[#fff8e7] text-[#9a6400]"
            barClassName="bg-[#d99511]"
          />
        </div>

        <section className="overflow-hidden rounded-lg border border-[#d7e6eb] bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-[#e4eef2] px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.06em] text-[#17262f]">Inbox</p>
              <h2 className="mt-1 text-lg font-semibold text-[#17262f]">Feedback entries</h2>
            </div>
            {total > 0 ? (
              <span className="w-fit rounded-full border border-[#cde3e9] bg-[#f5fafc] px-3 py-1 text-xs font-semibold text-[#167f9d]">
                {pageStartIndex}-{pageEndIndex} of {total}
              </span>
            ) : null}
          </div>

          <div className="border-b border-[#eef4f6] px-4 py-4 sm:px-6">
            <div className="grid gap-3 xl:grid-cols-[minmax(14rem,1.2fr)_minmax(14rem,1fr)_minmax(13rem,0.9fr)]">
              <label className="space-y-1.5">
                <span className="text-xs font-bold uppercase tracking-[0.05em] text-[#17262f]">Search</span>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6b7c85]" />
                  <Input
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      resetPage();
                    }}
                    placeholder="Title, notes, client, or plan"
                    className="bg-white pl-9"
                  />
                </div>
              </label>
              <label className="space-y-1.5">
                <span className="text-xs font-bold uppercase tracking-[0.05em] text-[#17262f]">Nutritionist</span>
                <Input
                  value={nutritionistEmail}
                  onChange={(event) => {
                    setNutritionistEmail(event.target.value);
                    resetPage();
                  }}
                  placeholder="Email address"
                  className="bg-white"
                />
              </label>
              <div className="space-y-1.5">
                <span className="text-xs font-bold uppercase tracking-[0.05em] text-[#17262f]">Context</span>
                <Select
                  value={feedbackType}
                  onValueChange={(value) => {
                    setFeedbackType(value);
                    resetPage();
                  }}
                >
                  <SelectTrigger className="border-[#cde3e9] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All contexts</SelectItem>
                    {FEEDBACK_CONTEXT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div className="grid gap-3 sm:grid-cols-[minmax(9rem,11rem)_minmax(9rem,11rem)_7rem]">
                <label className="space-y-1.5">
                  <span className="text-xs font-bold uppercase tracking-[0.05em] text-[#17262f]">From</span>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(event) => {
                      setDateFrom(event.target.value);
                      resetPage();
                    }}
                    className="bg-white"
                  />
                </label>
                <label className="space-y-1.5">
                  <span className="text-xs font-bold uppercase tracking-[0.05em] text-[#17262f]">To</span>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(event) => {
                      setDateTo(event.target.value);
                      resetPage();
                    }}
                    className="bg-white"
                  />
                </label>
                <div className="space-y-1.5">
                  <span className="text-xs font-bold uppercase tracking-[0.05em] text-[#17262f]">Rows</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(value) => {
                      setPageSize(Number(value));
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="h-10 w-full border-[#cde3e9] bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((option) => (
                        <SelectItem key={option} value={String(option)}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="border-[#d7e6eb] bg-[#f7fbfc] px-3 py-1.5 text-[#5d6f78]">
                  <Filter className="mr-1.5 h-3.5 w-3.5" />
                  {hasActiveFilters ? 'Filtered' : 'All feedback'}
                </Badge>
                {activeFilterChips.map((chip) => (
                  <Badge key={chip} variant="outline" className="max-w-[18rem] border-[#cde3e9] bg-white px-3 py-1.5 text-[#334650]">
                    <span className="truncate">{chip}</span>
                  </Badge>
                ))}
                {hasActiveFilters ? (
                  <Button type="button" variant="outline" className="bg-white" onClick={clearFilters}>
                    <X className="h-4 w-4" />
                    Clear
                  </Button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="divide-y divide-[#edf3f5]">
            {isLoading && items.length === 0 ? (
              <div className="flex items-center justify-center gap-2 px-4 py-16 text-sm text-[#5d6f78]">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading feedback
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 px-4 py-16 text-center text-sm text-[#5d6f78]">
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#eef5f8] text-[#94a8b0]">
                  <MessageSquareText className="h-6 w-6" />
                </span>
                <div>
                  <p className="font-medium text-[#334650]">No feedback found</p>
                  <p className="mt-1 text-xs text-[#8a9aa2]">Try adjusting the filters.</p>
                </div>
              </div>
            ) : (
              items.map((item) => {
                const state = feedbackStateMeta(item);
                const StateIcon = state.icon;

                return (
                  <article
                    key={item.id}
                    className={`grid gap-4 border-l-4 px-4 py-4 transition-colors sm:px-6 xl:grid-cols-[minmax(14rem,0.8fr)_minmax(0,1.45fr)_minmax(12rem,0.45fr)] xl:items-start ${state.rowClassName}`}
                  >
                    <div className="flex min-w-0 gap-3">
                      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${state.avatarClassName}`}>
                        {getInitials(item.nutritionistEmail)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-[#17262f]">{item.nutritionistEmail}</p>
                        <p className="mt-1 flex items-center gap-1 text-xs text-[#6b7c85]">
                          <UserRound className="h-3.5 w-3.5" />
                          ID {formatShortId(item.nutritionistId)}
                        </p>
                      </div>
                    </div>

                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className={state.badgeClassName}>
                          <StateIcon className="mr-1 h-3.5 w-3.5" />
                          {state.label}
                        </Badge>
                        <Badge variant="outline" className="max-w-full border-[#cde3e9] bg-[#f7fbfc] text-[#334650]">
                          <span className="truncate">{formatContext(item.feedbackType)}</span>
                        </Badge>
                      </div>
                      {item.title ? (
                        <p className="mt-3 truncate text-sm font-semibold text-[#17262f]" title={item.title}>
                          {item.title}
                        </p>
                      ) : null}
                      <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-[#334650]">{item.feedbackText}</p>
                      {(item.clientId || item.mealPlanId || item.feedbackDate) && (
                        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-[#6b7c85]">
                          {item.feedbackDate ? (
                            <span className="inline-flex items-center gap-1 rounded-md bg-[#f7fbfc] px-2 py-1">
                              <CalendarDays className="h-3.5 w-3.5" />
                              Feedback date {formatDateOnly(item.feedbackDate)}
                            </span>
                          ) : null}
                          {item.clientId ? (
                            <span className="rounded-md bg-[#f7fbfc] px-2 py-1">Client {formatShortId(item.clientId)}</span>
                          ) : null}
                          {item.mealPlanId ? (
                            <span className="rounded-md bg-[#f7fbfc] px-2 py-1">Plan {formatShortId(item.mealPlanId)}</span>
                          ) : null}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 xl:flex-col xl:items-end">
                      <RatingDisplay rating={item.rating} />
                      <span className="inline-flex items-center gap-1 text-xs text-[#6b7c85]">
                        <CalendarDays className="h-3.5 w-3.5" />
                        Submitted {formatDateTime(item.createdAt)}
                      </span>
                    </div>
                  </article>
                );
              })
            )}
          </div>

          {total > 0 ? (
            <div className="flex flex-col gap-3 border-t border-[#eef4f6] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <p className="text-sm text-[#5d6f78]">
                Showing <span className="font-semibold text-[#17262f]">{pageStartIndex}-{pageEndIndex}</span> of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || isLoading}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="min-w-20 text-center text-sm text-[#5d6f78]">
                  {page} / {totalPages}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || isLoading}
                  onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : null}
        </section>
      </div>
    </main>
  );
};

export default NutritionistFeedbackPage;
